import React, { useState, useEffect, useRef, useMemo, useCallback, useContext } from "react";
import { collection, getDocs, query, orderBy, doc, setDoc, deleteDoc, getDoc, updateDoc } from "firebase/firestore";
import { db, auth } from "../firebase";
import { FaXTwitter, FaThumbsUp, FaBan } from "react-icons/fa6";
import defaultAvatar from "../assets/default-avatar.png";
import UserContext from "../context/UserContext";

// Error Boundary Component
const FeedErrorBoundary = ({ children }) => {
  const [hasError] = useState(false);

  useEffect(() => {
    if (hasError) {
      console.error("Feed component encountered an error. Please refresh or try again.");
    }
  }, [hasError]);

  if (hasError) {
    return (
      <div className="p-4 max-w-2xl mx-auto bg-silver rounded-custom shadow-lg text-center">
        <h2 className="text-xl font-bold text-red-600">Error Loading Feed</h2>
        <p className="text-gray-700">Something went wrong. Please refresh the page or try again later.</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-4 bg-kids-blue text-white px-4 py-2 rounded-custom hover:bg-blue-600"
        >
          Refresh
        </button>
      </div>
    );
  }

  return children;
};

const Feed = () => {
  const { user } = useContext(UserContext);
  const [feed, setFeed] = useState([]);
  const [loading, setLoading] = useState(true); // Start loading immediately
  const [error, setError] = useState(null);
  const [visibleCount, setVisibleCount] = useState(15); // Initial load of 15 items
  const [likes, setLikes] = useState({}); // Track likes for each post
  const [filters, setFilters] = useState({
    nasaNews: true,
    nasaImage: true,
    nasaApod: true,
  });
  const feedEndRef = useRef(null); // Ref for detecting scroll position
  const isMountedRef = useRef(true); // Track if component is mounted



  useEffect(() => {
    const loadFeed = async () => {
      if (!auth.currentUser) {
        setError("Please log in to view the feed.");
        setLoading(false);
        return;
      }
      try {
        const q = query(collection(db, "Feed"), orderBy("created_date", "desc"));
        const snapshot = await getDocs(q);
        const feedData = snapshot.docs
          .map((doc) => ({ id: doc.id, ...doc.data() }))
          .filter((item) => !item.blocked); // Exclude blocked items
        setFeed(feedData);

        // Extract unique YouTube creators and initialize dynamic filters
        const youtubeCreators = new Set();
        feedData.forEach((item) => {
          if (item.source === "YouTube") {
            // Extract creator name from document ID (format: "CreatorName_VideoId")
            const creatorName = item.id.split('_')[0];
            if (creatorName) {
              youtubeCreators.add(creatorName);
            }
          }
        });

        // Initialize dynamic filters for YouTube creators
        const dynamicFilters = {};
        youtubeCreators.forEach((creator) => {
          // Convert creator name to camelCase for filter key
          const filterKey = creator.toLowerCase().replace(/\s+/g, '');
          dynamicFilters[filterKey] = true;
        });

        setFilters(prev => ({
          ...prev,
          ...dynamicFilters
        }));

        setError(null); // Clear error on success
      } catch (err) {
        console.error("Error loading feed:", err);
        setError("Failed to load feed. Check permissions or try again later.");
      } finally {
        setLoading(false);
      }
    };
    loadFeed();
  }, []);

  const sourceMap = {
    YouTube: {
      creators: {
        SpaceX: "UCtI0Hodo5o5dUb67FeUjDeA",
        "Everyday Astronaut": "UC6uKrU_WqRa6Cdn4i8IArOA",
        "Scott Manley": "UCoO-1Jo4dXtxW8DKOxsk1uQ",
        "Rocket Lab": "UCNB3o-WMIIq7Q8T4oQ7CeAA",
      },
      baseUrl: "https://www.youtube.com/channel/",
      avatarUrl: (channelId) =>
        `https://yt3.ggpht.com/ytc/${channelId.split("").slice(-2).join("")}=s88-c-k-c0x00ffffff-no-rj`,
    },
    X: {
      creators: { SpaceX: "SpaceX", Erdayastronaut: "erdayastronaut", RocketLab: "RocketLab" },
      baseUrl: "https://x.com/",
      avatarUrl: (username) => `https://unavatar.io/twitter/${username}?fallback=false`,
    },
    NASA: {
      creators: { NASA: "NASA" },
      baseUrl: "https://www.nasa.gov/",
      avatarUrl: () => "https://www.nasa.gov/wp-content/uploads/2023/04/nasa-logo-web-rgb.png",
    },
  };

  const formatTimestamp = (dateOrTimestamp) => {
    if (dateOrTimestamp && dateOrTimestamp.toDate) {
      return dateOrTimestamp.toDate().toLocaleString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    } else if (dateOrTimestamp && dateOrTimestamp.seconds) {
      return new Date(dateOrTimestamp.seconds * 1000).toLocaleString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    }
    return "N/A";
  };



  // Immediate filter setter for direct updates
  const setFilterImmediate = useCallback((filterKey, value) => {
    setFilters(prev => ({ ...prev, [filterKey]: value }));
  }, []);

  // Function to select only one filter and deselect others
  const selectOnlyFilter = useCallback((selectedFilterKey) => {
    const allFilterKeys = Object.keys(filters);
    const newFilters = {};
    allFilterKeys.forEach(key => {
      newFilters[key] = key === selectedFilterKey;
    });
    setFilters(newFilters);
  }, [filters]);

  // Function to reset all filters to true (show all content)
  const resetFilters = useCallback(() => {
    const allFilterKeys = Object.keys(filters);
    const newFilters = {};
    allFilterKeys.forEach(key => {
      newFilters[key] = true;
    });
    setFilters(newFilters);
  }, [filters]);

  // Cleanup effect to set mounted flag to false
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Extract unique YouTube creators from feed data
  const youtubeCreators = useMemo(() => {
    const creators = new Set();
    feed.forEach((item) => {
      if (item.source === "YouTube") {
        // Extract creator name from document ID (format: "CreatorName_VideoId")
        const creatorName = item.id.split('_')[0];
        if (creatorName) {
          creators.add(creatorName);
        }
      }
    });
    return Array.from(creators).sort();
  }, [feed]);

  const filteredFeed = useMemo(() => {
    const shouldShowItem = (item) => {
      if (item.source === "NASA") {
        if (item.type === "APOD") {
          return filters.nasaApod;
        } else if (item.id.includes("Breaking News")) {
          return filters.nasaNews;
        } else {
          return filters.nasaImage;
        }
      } else if (item.source === "YouTube") {
        // Extract creator name from document ID
        const creatorName = item.id.split('_')[0];
        if (creatorName) {
          // Convert creator name to filter key format
          const filterKey = creatorName.toLowerCase().replace(/\s+/g, '');
          return filters[filterKey] !== false; // Default to true if filter doesn't exist
        }
        return false; // For items without proper creator name
      }
      return true; // Show X posts and other sources by default
    };

    return feed.filter(shouldShowItem);
  }, [feed, filters]);



  // Load likes for visible posts
  useEffect(() => {
    const loadLikes = async () => {
      if (!auth.currentUser) return;

      const visiblePosts = filteredFeed.slice(0, visibleCount);
      const likesData = {};

      for (const post of visiblePosts) {
        try {
          // Check if user has liked this post
          const likeRef = doc(db, "Feed", post.id, "likes", auth.currentUser.uid);
          const likeDoc = await getDoc(likeRef);
          const hasLiked = likeDoc.exists();

          // Get total likes count
          const likesCollection = collection(db, "Feed", post.id, "likes");
          const likesSnapshot = await getDocs(likesCollection);
          const likesCount = likesSnapshot.size;

          likesData[post.id] = {
            hasLiked,
            count: likesCount
          };
        } catch (error) {
          console.error("Error loading likes for post:", post.id, error);
          likesData[post.id] = { hasLiked: false, count: 0 };
        }
      }

      setLikes(likesData);
    };

    if (filteredFeed.length > 0) {
      loadLikes();
    }
  }, [filteredFeed, visibleCount]);

  const handleLike = async (postId) => {
    if (!auth.currentUser) return;

    const likeRef = doc(db, "Feed", postId, "likes", auth.currentUser.uid);
    const currentLike = likes[postId];

    try {
      if (currentLike?.hasLiked) {
        // Unlike the post
        await deleteDoc(likeRef);
        setLikes(prev => ({
          ...prev,
          [postId]: {
            hasLiked: false,
            count: Math.max(0, (prev[postId]?.count || 0) - 1)
          }
        }));
      } else {
        // Like the post
        await setDoc(likeRef, {
          userId: auth.currentUser.uid,
          timestamp: new Date()
        });
        setLikes(prev => ({
          ...prev,
          [postId]: {
            hasLiked: true,
            count: (prev[postId]?.count || 0) + 1
          }
        }));
      }
    } catch (error) {
      console.error("Error toggling like:", error);
    }
  };

  const handleBlock = async (postId) => {
    if (!auth.currentUser || user?.email !== "adamkfrancis1989@gmail.com") return;

    try {
      const postRef = doc(db, "Feed", postId);
      const postDoc = await getDoc(postRef);

      if (postDoc.exists()) {
        const currentBlocked = postDoc.data().blocked || false;
        await updateDoc(postRef, {
          blocked: !currentBlocked
        });

        // Remove from local feed state immediately
        setFeed(prev => prev.filter(item => item.id !== postId));
        console.log(`${currentBlocked ? 'Unblocked' : 'Blocked'} post: ${postId}`);
      }
    } catch (error) {
      console.error("Error toggling block:", error);
    }
  };

  // Reset visible count when filters change
  useEffect(() => {
    setVisibleCount(15);
  }, [filters]);

  useEffect(() => {
    const handleScroll = () => {
      if (
        feedEndRef.current &&
        window.innerHeight + window.scrollY >= feedEndRef.current.offsetTop &&
        visibleCount < filteredFeed.length
      ) {
        setVisibleCount((prevCount) => Math.min(prevCount + 15, filteredFeed.length));
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [visibleCount, filteredFeed.length]);

  if (loading) return <div className="p-4 text-center">Loading feed...</div>;
  if (error) return <div className="p-4 text-center text-red-600">{error}</div>;

  return (
    <FeedErrorBoundary>
      <div className="flex p-4 max-w-6xl mx-auto gap-6">
        {/* Filter Pane */}
        <div className="w-64 bg-white p-4 rounded-custom shadow-lg h-fit sticky top-4">
          <h3 className="text-lg font-bold mb-4 text-gray-900">Filter Content</h3>
          <div className="space-y-3">
            <div>
              <h4 className="font-semibold mb-2 text-gray-900">NASA</h4>
              <div className="space-y-2 ml-2">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={filters.nasaNews}
                    onChange={(e) => setFilterImmediate('nasaNews', e.target.checked)}
                    className="mr-2"
                  />
                  <span
                    className="text-sm text-gray-800 cursor-pointer hover:text-blue-600"
                    onClick={() => selectOnlyFilter('nasaNews')}
                  >
                    NASA News
                  </span>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={filters.nasaImage}
                    onChange={(e) => setFilterImmediate('nasaImage', e.target.checked)}
                    className="mr-2"
                  />
                  <span
                    className="text-sm text-gray-800 cursor-pointer hover:text-blue-600"
                    onClick={() => selectOnlyFilter('nasaImage')}
                  >
                    NASA Image of the Day
                  </span>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={filters.nasaApod}
                    onChange={(e) => setFilterImmediate('nasaApod', e.target.checked)}
                    className="mr-2"
                  />
                  <span
                    className="text-sm text-gray-800 cursor-pointer hover:text-blue-600"
                    onClick={() => selectOnlyFilter('nasaApod')}
                  >
                    NASA Astronomy Picture of the Day
                  </span>
                </div>
              </div>
            </div>
            {youtubeCreators.length > 0 && (
              <div>
                <h4 className="font-semibold mb-2 text-gray-900">YouTubers</h4>
                <div className="space-y-2 ml-2">
                  {youtubeCreators.map((creator) => {
                    const filterKey = creator.toLowerCase().replace(/\s+/g, '');
                    return (
                      <div key={creator} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={filters[filterKey] !== false}
                          onChange={(e) => setFilterImmediate(filterKey, e.target.checked)}
                          className="mr-2"
                        />
                        <span
                          className="text-sm text-gray-800 cursor-pointer hover:text-blue-600"
                          onClick={() => selectOnlyFilter(filterKey)}
                        >
                          {creator}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            <div className="pt-4 border-t border-gray-200">
              <button
                onClick={resetFilters}
                className="w-full bg-kids-blue text-white px-4 py-2 rounded-custom hover:bg-blue-600 transition-colors text-sm font-medium"
              >
                Reset Filters
              </button>
            </div>
          </div>
        </div>

        {/* Feed Content */}
        <div className="flex-1">
          <div className="space-y-6">
            {filteredFeed.slice(0, visibleCount).map((item, index) => {
            const sourceData = sourceMap[item.source];
            let creatorName = "Unknown";
            let channelId = null;

            if (item.source === "YouTube") {
              // Extract creator name from document ID for dynamic channels
              creatorName = item.id.split('_')[0] || "Unknown";

              // Try to find channel ID from sourceMap first
              const knownCreator = Object.keys(sourceData.creators).find((key) => item.id.includes(key.replace(/\s+/g, '')));
              if (knownCreator) {
                channelId = sourceData.creators[knownCreator];
                creatorName = knownCreator;
              } else {
                // For dynamic channels not in sourceMap, use a default channel ID
                channelId = "UCBR8-60-B28hp2BmDPdntcQ"; // YouTube's main channel as fallback
              }
            } else {
              creatorName = Object.keys(sourceData.creators).find((key) => item.id.includes(key)) || "Unknown";
              channelId = sourceData.creators[creatorName];
            }

            const profileUrl = channelId ? `${sourceData.baseUrl}${channelId}` : "#";
            const avatarUrl = channelId ? sourceData.avatarUrl(channelId) : defaultAvatar;
            const postUrl = item.url;

            return (
              <div key={item.id} className="bg-silver p-4 rounded-custom shadow-lg">
                {item.source === "YouTube" ? (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                        {creatorName}
                      </span>
                    </div>
                    <div className="relative w-full bg-gray-100 rounded-custom overflow-hidden" style={{ paddingBottom: "56.25%" }}>
                      {(() => {
                        // Extract video ID more robustly
                        let videoId = "";
                        try {
                          if (item.url && item.url.includes("v=")) {
                            videoId = item.url.split("v=")[1].split("&")[0] || "";
                          } else if (item.url && item.url.includes("youtu.be/")) {
                            videoId = item.url.split("youtu.be/")[1].split("?")[0] || "";
                          }
                        } catch (error) {
                          console.error("Error extracting video ID:", error);
                        }

                        return videoId ? (
                          <div className="absolute inset-0 w-full h-full">
                            <iframe
                              src={`https://www.youtube.com/embed/${videoId}`}
                              title={item.title || "YouTube video"}
                              className="w-full h-full rounded-custom"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                              loading="lazy"
                            ></iframe>
                          </div>
                        ) : (
                          <div className="absolute inset-0 w-full h-full bg-gray-200 flex items-center justify-center">
                            <div className="text-center text-gray-600">
                              <p className="text-sm">Video unavailable</p>
                              <a
                                href={item.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:underline text-sm"
                              >
                                Watch on YouTube
                              </a>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                    <div className="mt-3 flex items-center justify-between">
                      <span className="text-sm text-gray-500">{formatTimestamp(item.created_date)}</span>
                      <div className="flex items-center space-x-2">
                        {user?.email === "adamkfrancis1989@gmail.com" && (
                          <button
                            onClick={() => handleBlock(item.id)}
                            className="flex items-center space-x-1 px-3 py-1 rounded-full text-red-600 hover:text-red-800 hover:bg-red-50 transition-colors"
                            title="Block this post"
                          >
                            <FaBan className="text-sm" />
                          </button>
                        )}
                        <button
                          onClick={() => handleLike(item.id)}
                          className={`flex items-center space-x-1 px-3 py-1 rounded-full transition-colors ${
                            likes[item.id]?.hasLiked
                              ? "text-blue-600 bg-blue-50"
                              : "text-gray-500 hover:text-blue-600 hover:bg-blue-50"
                          }`}
                        >
                          <FaThumbsUp
                            className={`text-sm ${likes[item.id]?.hasLiked ? "fill-current" : ""}`}
                          />
                          <span className="text-sm">{likes[item.id]?.count || 0}</span>
                        </button>
                      </div>
                    </div>
                  </div>
                ) : item.source === "NASA" ? (
                  <div className="bg-white p-3 rounded-custom shadow-md border border-gray-200">
                    <div className="flex items-start">
                      <img
                        src={avatarUrl || defaultAvatar}
                        alt={`${creatorName}'s avatar`}
                        className="w-10 h-10 rounded-full mr-3 object-contain"
                        onError={(e) => (e.target.src = defaultAvatar)}
                      />
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <a
                            href={profileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-bold text-text-accent hover:underline"
                          >
                            {creatorName}
                          </a>
                          {item.type === "APOD" && (
                            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                              Astronomy Picture of the Day
                            </span>
                          )}
                          {item.id.includes("Breaking News") && (
                            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                              News
                            </span>
                          )}
                          {!item.type && !item.id.includes("Breaking News") && (
                            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                              Image of the Day
                            </span>
                          )}
                        </div>
                        <p className="text-black break-words font-semibold">{item.title}</p>
                        {item.image && (
                          <div className="mt-2">
                            {item.url && item.url.includes('.mp4') ? (
                              <video
                                src={item.url}
                                controls
                                className="w-full h-auto rounded-custom"
                                poster={item.image}
                              >
                                Your browser does not support the video tag.
                              </video>
                            ) : (
                              <a href={postUrl} target="_blank" rel="noopener noreferrer">
                                <img
                                  src={item.image}
                                  alt={item.title}
                                  className="w-full h-auto rounded-custom"
                                  onError={(e) => (e.target.style.display = "none")}
                                />
                              </a>
                            )}
                          </div>
                        )}
                        {item.explanation && (
                          <div className="mt-3 p-3 bg-gray-50 rounded-custom">
                            <p className="text-sm text-gray-700 leading-relaxed">{item.explanation}</p>
                          </div>
                        )}
                        {item.type === "APOD" && (!item.url || !item.url.match(/\.(jpg|jpeg|png|gif|webp)$/i)) && (
                          <div className="mt-2">
                            {(() => {
                              // Generate APOD archive URL from created_date
                              let archiveUrl = "";
                              try {
                                const date = item.created_date?.toDate ? item.created_date.toDate() : new Date(item.created_date?.seconds * 1000);
                                const year = date.getFullYear().toString().slice(-2);
                                const month = (date.getMonth() + 1).toString().padStart(2, '0');
                                const day = date.getDate().toString().padStart(2, '0');
                                archiveUrl = `https://apod.nasa.gov/apod/ap${year}${month}${day}.html`;
                              } catch (error) {
                                console.error("Error generating APOD archive URL:", error);
                                archiveUrl = "https://apod.nasa.gov/apod/";
                              }

                              return (
                                <a
                                  href={archiveUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-block bg-kids-blue text-white px-4 py-2 rounded-custom hover:bg-blue-600 transition-colors"
                                >
                                  View on NASA website
                                </a>
                              );
                            })()}
                          </div>
                        )}
                        {item.url && item.id.includes("Breaking News") && (
                          <a
                            href={postUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-2 inline-block bg-kids-blue text-white px-4 py-2 rounded-custom hover:bg-blue-600"
                          >
                            View News Post
                          </a>
                        )}
                        <div className="mt-2 flex items-center justify-between">
                          <span className="text-sm text-gray-500">{formatTimestamp(item.created_date)}</span>
                          <div className="flex items-center space-x-2">
                            {user?.email === "adamkfrancis1989@gmail.com" && (
                              <button
                                onClick={() => handleBlock(item.id)}
                                className="flex items-center space-x-1 px-3 py-1 rounded-full text-red-600 hover:text-red-800 hover:bg-red-50 transition-colors"
                                title="Block this post"
                              >
                                <FaBan className="text-sm" />
                              </button>
                            )}
                            <button
                              onClick={() => handleLike(item.id)}
                              className={`flex items-center space-x-1 px-3 py-1 rounded-full transition-colors ${
                                likes[item.id]?.hasLiked
                                  ? "text-blue-600 bg-blue-50"
                                  : "text-gray-500 hover:text-blue-600 hover:bg-blue-50"
                              }`}
                            >
                              <FaThumbsUp
                                className={`text-sm ${likes[item.id]?.hasLiked ? "fill-current" : ""}`}
                              />
                              <span className="text-sm">{likes[item.id]?.count || 0}</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-white p-3 rounded-custom shadow-md border border-gray-200">
                    <div className="flex items-start">
                      <img
                        src={avatarUrl || defaultAvatar}
                        alt={`${creatorName}'s avatar`}
                        className="w-10 h-10 rounded-full mr-3 object-cover"
                        onError={(e) => (e.target.src = defaultAvatar)}
                      />
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <a
                            href={profileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-bold text-text-accent hover:underline"
                          >
                            {creatorName}
                          </a>
                          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                            X Post
                          </span>
                          <span className="text-gray-500 text-sm">@{creatorName.toLowerCase()}</span>
                        </div>
                        <p className="text-black break-words">{item.title}</p>
                        <div className="mt-2">
                          <img
                            src="https://via.placeholder.com/300x150"
                            alt="X Post Media"
                            className="w-full h-auto rounded-custom"
                          />
                        </div>
                        <div className="mt-2 flex items-center justify-between">
                          <div className="flex items-center space-x-4">
                            <span className="text-sm text-gray-500">{formatTimestamp(item.created_date)}</span>
                            <a href={postUrl} target="_blank" rel="noopener noreferrer" className="flex items-center text-gray-500 hover:text-blue-600">
                              <FaXTwitter className="mr-1" /> View Post
                            </a>
                          </div>
                          <div className="flex items-center space-x-2">
                            {user?.email === "adamkfrancis1989@gmail.com" && (
                              <button
                                onClick={() => handleBlock(item.id)}
                                className="flex items-center space-x-1 px-3 py-1 rounded-full text-red-600 hover:text-red-800 hover:bg-red-50 transition-colors"
                                title="Block this post"
                              >
                                <FaBan className="text-sm" />
                              </button>
                            )}
                            <button
                              onClick={() => handleLike(item.id)}
                              className={`flex items-center space-x-1 px-3 py-1 rounded-full transition-colors ${
                                likes[item.id]?.hasLiked
                                  ? "text-blue-600 bg-blue-50"
                                  : "text-gray-500 hover:text-blue-600 hover:bg-blue-50"
                              }`}
                            >
                              <FaThumbsUp
                                className={`text-sm ${likes[item.id]?.hasLiked ? "fill-current" : ""}`}
                              />
                              <span className="text-sm">{likes[item.id]?.count || 0}</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          {visibleCount < filteredFeed.length && <div ref={feedEndRef} />}
          {error && <div className="p-4 text-center text-red-600">{error}</div>}
          </div>
        </div>
      </div>
    </FeedErrorBoundary>
  );
};

export default Feed;
